import { type RecordId } from '../types';
import { ApplicationController } from '@aha-app/mvc';
import Debug from 'debug';
import WhiteboardController from './WhiteboardController';
import ToolPaste from '../tools/ToolPaste';
import { ToolFitToScreen } from '../tools/ToolFitToScreen';
import { type IToolActionInput } from '../tools/Tool';
import ToolDelete from '../tools/ToolDelete';

const debug = Debug('whiteboard');

interface HistoryControllerState {
  whiteboardVersions: Aha.WhiteboardVersion[] | null;
  previewVersionId: RecordId | null;
  previewData: any;
  fetchingPreview: boolean;
  flashLatestVersion: boolean;
}

export interface HistoryDataManager {
  fetchWhiteboardVersions: (
    whiteboardId: RecordId
  ) => Promise<Aha.WhiteboardVersion[]>;
  fetchWhiteboardVersion: (
    whiteboardId: RecordId,
    whiteboardVersionId: RecordId
  ) => Promise<Aha.WhiteboardVersion>;
  createVersionSnapshot: (whiteboardId: RecordId) => Promise<Response>;
}

export interface HistoryControllerProps {
  whiteboardId: RecordId;
  historyDataManager: HistoryDataManager;
}

export default class HistoryController extends ApplicationController<
  HistoryControllerState,
  HistoryControllerProps
> {
  async initialize() {
    this.state.previewVersionId = null;
    this.state.fetchingPreview = false;
    this.state.flashLatestVersion = false;
  }

  allowedToRevert() {
    const whiteboardController =
      this.findControllerInstance(WhiteboardController)!;
    return (
      !whiteboardController.isReadOnly() &&
      whiteboardController.props.allowUpdate
    );
  }

  async actionGetWhiteboardVersions() {
    this.state.flashLatestVersion = false;

    const whiteboardVersions =
      await this.props.historyDataManager.fetchWhiteboardVersions(
        this.props.whiteboardId
      );
    this.state.whiteboardVersions = whiteboardVersions.reverse();
  }

  async actionSaveVersion() {
    debug('Creating a snapshot of the current version');

    // Start by emptying the whiteboardVersions so a loading indicator is shown
    this.state.whiteboardVersions = null;

    await this.actionCreateVersionSnapshot();

    // Reload the whiteboard versions so we pick up the newly created version
    await this.actionGetWhiteboardVersions();

    this.state.flashLatestVersion = true;
  }

  async actionRevertVersion(versionId: RecordId) {
    debug('Reverting to version', versionId);

    window.AhaAnalytics?.track(
      'Feature-7240252275556413975',
      'User restores whiteboard history',
      {}
    );

    this.actionClosePreview();

    const whiteboardVersion =
      await this.props.historyDataManager.fetchWhiteboardVersion(
        this.props.whiteboardId,
        versionId
      );

    const oldContent = whiteboardVersion.editorContent;

    const whiteboardController =
      this.findControllerInstance(WhiteboardController)!;

    // Start by emptying the whiteboardVersions so a loading indicator is shown
    this.state.whiteboardVersions = null;

    await this.actionCreateVersionSnapshot();

    // Bad things happen if we try and delete objects that are selected
    whiteboardController.canvas.discardActiveObject();

    // Delete all items on the whiteboard
    ToolDelete.deleteObjects(
      whiteboardController.canvas.getObjects(),
      whiteboardController
    );

    // Add all the items from the old content
    // This probably isn't going to handle images. Look at the action of
    // ToolPaste to see how it uses readImage
    await ToolPaste.pasteContents({
      contents: oldContent,
      controller: whiteboardController,
      position: {
        x: 0,
        y: 0,
      },
    });

    whiteboardController.canvas.requestRenderAll();

    // Tools expect a selection, but this tool doesn't use it. We should pull
    // the ToolFitToScreen.action guts into a separate function so we can call
    // it directly without type casting this param.
    ToolFitToScreen.action({
      controller: whiteboardController,
    } as IToolActionInput);

    // Reload the whiteboard versions so we pick up the newly created version
    await this.actionGetWhiteboardVersions();
  }

  async actionPreviewVersion(versionId: RecordId) {
    debug('Previewing version', versionId);

    window.AhaAnalytics?.track(
      'Feature-7240252275556413975',
      'User previews whiteboard history',
      {}
    );

    this.state.fetchingPreview = true;

    const whiteboardVersion =
      await this.props.historyDataManager.fetchWhiteboardVersion(
        this.props.whiteboardId,
        versionId
      );

    this.state.previewVersionId = versionId;
    this.state.previewData = whiteboardVersion.editorContent;
    this.state.fetchingPreview = false;
  }

  actionClosePreview() {
    this.state.previewVersionId = null;
    this.state.previewData = null;
  }

  private async actionCreateVersionSnapshot() {
    // Make a checkpoint of the current version. This will use whatever the
    // server has at the time, which is possibly slightly desynced from what
    // this client has. It will only be a few seconds worth of content at most,
    // so the difference is ok.
    // Call the endpoint POST /whiteboards/:whiteboardId/whiteboard_version using fetch
    // and make sure it is successful before proceeding
    const response = await this.props.historyDataManager.createVersionSnapshot(
      this.props.whiteboardId
    );

    if (!response.ok) {
      throw new Error(
        `Error creating whiteboard version snapshot! Status: ${response.status}`
      );
    }
  }
}
